/**
 * Dashboard sportif — §D. Répond aux 5 questions clés :
 *
 *   Q1. Est-ce que je respecte ma routine ?   → compliance, streak, fréquences
 *   Q2. Est-ce que je progresse ?             → objectifs, charge, poids, équilibre
 *   Q3. Est-ce que je récupère assez ?        → signaux récup + charge semaine
 *   Q4. Quel est mon prochain entraînement ?  → reco coach (ai_coach)
 *   Q5. Qu'est-ce que je dois corriger ?      → conseils actifs du moteur
 *
 * Les chiffres viennent du backend (progress_engine) ; on ne fait qu'afficher.
 */
import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'

import { performanceApi } from '../api/performanceApi'
import {
  Loading,
  PERF_ACCENT,
  PERF_ACCENT_2,
  ProgressBar,
  RECOVERY_LABELS,
  SEVERITY_COLOR,
  SectionTitle,
  StatCard,
  cardSx,
} from './shared'

function MiniBars({ series, accent = PERF_ACCENT, unitKey = 'load' }) {
  const vals = series.map((d) => d[unitKey] || 0)
  const max = Math.max(1, ...vals)
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 90 }}>
      {series.map((d, i) => (
        <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
          <Box
            sx={{
              height: `${((d[unitKey] || 0) / max) * 70}px`,
              minHeight: 2,
              bgcolor: accent,
              borderRadius: 1,
              opacity: 0.85,
            }}
          />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, mt: 0.5 }}>
            {(d.week_start || '').slice(5)}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

export default function DashboardView() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      const d = await performanceApi.getDashboard()
      setData(d)
      setLoading(false)
    })()
  }, [])

  if (loading) return <Loading label="Analyse de votre semaine sportive…" />
  if (!data)
    return (
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', py: 4 }}>
        Dashboard indisponible pour le moment.
      </Typography>
    )

  const routine = data.routine || {}
  const progress = data.progress || {}
  const recovery = data.recovery || {}
  const comp = routine.compliance || {}
  const next = data.next_session || {}
  const toCorrect = data.to_correct || []
  const signals = recovery.signals || {}

  return (
    <Box>
      {/* Q1 — Routine */}
      <SectionTitle>Est-ce que je respecte ma routine ?</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Compliance"
            value={comp.compliance_pct != null ? comp.compliance_pct : '—'}
            suffix={comp.compliance_pct != null ? '%' : ''}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Séances réalisées"
            value={`${comp.completed ?? 0}/${comp.planned ?? 0}`}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Série en cours"
            value={routine.streak_days ?? 0}
            suffix=" j"
            accent={PERF_ACCENT_2}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Charge semaine"
            value={recovery.this_week_load ?? 0}
            accent={PERF_ACCENT_2}
          />
        </Grid>
      </Grid>
      {(routine.frequency_targets || []).length > 0 && (
        <Box sx={{ ...cardSx, mb: 3 }}>
          <Stack spacing={1.5}>
            {routine.frequency_targets.map((f) => (
              <Box key={f.sport}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography sx={{ color: '#fff', fontSize: 14 }}>
                    {f.sport}
                  </Typography>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}
                  >
                    {f.done}/{f.target} séances
                  </Typography>
                </Stack>
                <ProgressBar pct={f.target ? (f.done / f.target) * 100 : 0} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Q2 — Progression */}
      <SectionTitle>Est-ce que je progresse ?</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...cardSx, height: '100%' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 1 }}>
              Objectifs prioritaires
            </Typography>
            {(progress.goals || []).length === 0 ? (
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                Aucun objectif actif. Définissez-en un dans l'onglet Objectifs.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {progress.goals.slice(0, 4).map((g) => (
                  <Box key={g.id}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography sx={{ color: '#fff', fontSize: 14 }}>
                        {g.title}
                      </Typography>
                      <Typography
                        sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}
                      >
                        {g.current ?? '—'} / {g.target ?? '—'} {g.unit}
                      </Typography>
                    </Stack>
                    <ProgressBar pct={g.progress_pct ?? 0} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...cardSx, height: '100%' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 1 }}>
              Charge hebdomadaire (6 semaines)
            </Typography>
            {(progress.load_trend || []).length > 0 ? (
              <MiniBars series={progress.load_trend} unitKey="load" />
            ) : (
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                Pas encore de données de charge.
              </Typography>
            )}
            {(progress.sport_balance || []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 1 }}
                >
                  Équilibre entre sports (4 sem.)
                </Typography>
                <Stack spacing={0.75}>
                  {progress.sport_balance.map((b) => (
                    <Stack
                      key={b.sport}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography sx={{ color: '#fff', fontSize: 13 }}>
                        {b.sport}
                      </Typography>
                      <Typography sx={{ color: PERF_ACCENT, fontSize: 13 }}>
                        {b.share_pct}%
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Q3 — Récupération */}
      <SectionTitle>Est-ce que je récupère assez ?</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        {Object.keys(signals).length === 0 ? (
          <Grid item xs={12}>
            <Box sx={cardSx}>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                Aucun signal de récupération. Connectez le module Santé ou une
                intégration (Garmin) dans Réglages pour suivre sommeil, FC au
                repos et HRV.
              </Typography>
            </Box>
          </Grid>
        ) : (
          Object.entries(signals).map(([metric, v]) => (
            <Grid item xs={6} sm={3} key={metric}>
              <StatCard
                label={RECOVERY_LABELS[metric] || metric}
                value={v.avg}
                accent={PERF_ACCENT_2}
              />
            </Grid>
          ))
        )}
      </Grid>
      {recovery.disengagement?.at_risk && (
        <Box
          sx={{
            ...cardSx,
            mb: 3,
            borderLeft: `4px solid ${SEVERITY_COLOR.warning}`,
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 14 }}>
            Décrochage détecté : {recovery.disengagement.days_since_last} jours
            sans activité. Une courte séance suffit à relancer la dynamique.
          </Typography>
        </Box>
      )}

      {/* Q4 — Prochain entraînement */}
      <SectionTitle>Quel est mon prochain entraînement optimal ?</SectionTitle>
      <Box
        sx={{ ...cardSx, mb: 3, borderLeft: `4px solid ${PERF_ACCENT}`, mt: 1 }}
      >
        {next.session ? (
          <>
            <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
              {next.session.title} · {next.session.type} ·{' '}
              {next.session.date}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
              {next.explanation}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
            {next.explanation ||
              "Aucune séance planifiée. Générez votre planning dans l'onglet Planning."}
          </Typography>
        )}
      </Box>

      {/* Q5 — À corriger */}
      <SectionTitle>Qu'est-ce que je dois corriger ?</SectionTitle>
      {toCorrect.length === 0 ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
            Rien à signaler — votre charge et votre récupération sont cohérentes.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {toCorrect.map((a) => (
            <Box
              key={a.id}
              sx={{
                ...cardSx,
                borderLeft: `4px solid ${
                  SEVERITY_COLOR[a.severity] || PERF_ACCENT
                }`,
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                {a.title}
              </Typography>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5 }}
              >
                {a.message}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}
